import type { Vehicle } from "./api";

/**
 * Vehicle classes on the phone. Requirements 7.13 through 7.15.
 *
 * The grouping rule lives here rather than inline in the garage screen because it is real logic with a
 * real edge — case — and because it has to agree exactly with `AppModel.classes` on the television. Two
 * clients disagreeing about whether "hot wheels" and "Hot Wheels" are one class would show a family two
 * tabs on one device and one on the other, for the same garage.
 */

/** What a vehicle with no class races under. Requirement 7.14 — a real class, not a gap. */
export const UNCLASSIFIED = "Unclassified";

/** One vehicle's class label, with blank and whitespace-only treated as absent. */
export function classLabel(vehicle: Vehicle): string {
  const raw = (vehicle.vehicleClass ?? "").trim();
  return raw === "" ? UNCLASSIFIED : raw;
}

/**
 * Every class in a garage, in the order a family should meet them.
 *
 * Grouped case-insensitively and displayed with the **first spelling seen**, because requirement 7.14
 * creates a class by typing its name and a hurried "hot wheels" must join "Hot Wheels" rather than
 * start a rival that looks identical in a list. Sorted by name with unclassified last: it is where
 * vehicles arrive rather than somewhere anybody chose.
 *
 * Retired vehicles are excluded — a class that exists only in history is not one you can race.
 */
export function classesOf(vehicles: Vehicle[]): string[] {
  const firstSpelling = new Map<string, string>();
  for (const vehicle of vehicles) {
    if (vehicle.isRetired) continue;
    const label = classLabel(vehicle);
    const folded = label.toLowerCase();
    if (!firstSpelling.has(folded)) firstSpelling.set(folded, label);
  }
  const named = [...firstSpelling.values()]
    .filter((name) => name !== UNCLASSIFIED)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  return firstSpelling.has(UNCLASSIFIED.toLowerCase()) ? [...named, UNCLASSIFIED] : named;
}

/** Whether two class names are the same class. */
export function sameClass(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function inClass(vehicles: Vehicle[], name: string): Vehicle[] {
  return vehicles.filter((vehicle) => sameClass(classLabel(vehicle), name));
}

/**
 * What to send for a class the family chose.
 *
 * `UNCLASSIFIED` is this app's word for the *absence* of a class, so it must go over the wire as an
 * empty string — which the server reads as a deliberate clear — rather than as a class literally named
 * "Unclassified" that would then appear alongside the real ones.
 */
export function wireValue(name: string): string {
  return sameClass(name, UNCLASSIFIED) ? "" : name.trim();
}
